import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';
import { useFeatureSettings } from '@/hooks/useFeatureSettings';
import { PriceDisplay } from '@/components/PriceDisplay';
import { DiscountBadge } from '@/components/DiscountBadge';
import { ImagePresets } from '@/lib/image-optimizer';
import { useLanguage } from '@/contexts/LanguageContext';

interface RecentlyViewedProductsProps {
  excludeProductId?: string;
}

const RecentlyViewedProducts = ({ excludeProductId }: RecentlyViewedProductsProps) => {
  const { recentlyViewed, clearHistory } = useRecentlyViewed();
  const { data: features } = useFeatureSettings();
  const { language } = useLanguage();

  if (!features?.recently_viewed_enabled) return null;

  const items = recentlyViewed.filter(p => p.id !== excludeProductId);
  if (items.length === 0) return null;

  const texts = {
    title: language === 'lt' ? 'Neseniai peržiūrėti' : 'Recently Viewed',
    clear: language === 'lt' ? 'Išvalyti' : 'Clear',
  };

  return (
    <section className="py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          {texts.title}
        </h2>
        <Button variant="ghost" size="sm" onClick={clearHistory} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4 mr-1" />
          {texts.clear}
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {items.slice(0, 6).map((product) => (
          <Link
            key={product.id}
            to={`/produktas/${product.slug || product.id}`}
            className="block"
          >
          <Card
            className="group cursor-pointer overflow-hidden border border-border/50 hover:border-primary/30 hover:shadow-md transition-all duration-300"
          >
            <div className="relative aspect-square overflow-hidden bg-muted/30">
              {product.discount_percentage && product.discount_percentage > 0 && (
              <div className="absolute top-2 left-2 z-10">
                  <DiscountBadge discountPercentage={product.discount_percentage!} />
                </div>
              )}
              <img
                src={ImagePresets.productThumbnail(product.image)}
                alt={product.name}
                className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
            <div className="p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{product.brand}</p>
              <h3 className="text-sm font-medium line-clamp-2 mt-1 group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <div className="mt-2">
                <PriceDisplay
                  price={product.price}
                  discountPercentage={product.discount_percentage}
                />
              </div>
            </div>
          </Card>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RecentlyViewedProducts;
